/** Whether the signed-in tenant's onboarding is open, for the parts of the dashboard outside the
 *  Onboarding page: the menu entry, the banner on every other page and the landing redirect.
 *
 *  Read once per page load (`GET state`) by a user who may read it (`tour_operator.read`); the
 *  Onboarding page then publishes every state it polls (and its refusals), so the menu and banner
 *  follow it. A page load that starts on the Onboarding page leaves that first read to the page's own
 *  poll (one request, not two). "Closed" is final for a tenant — `409 not_easy` (not created by easy
 *  onboarding; most tenants) or a live go-live — so it is remembered in localStorage by tenant id
 *  (sharedContext) and later page loads skip the request. It outlives a sign-out (sharedContext.clear()
 *  only clears keys written in the same page load), which is harmless: closed never reopens. A failed
 *  read (5xx, network) leaves the status `unknown`: no banner, no redirect, nothing remembered. */
import * as React from 'react';

import { isDashboardChromeEmbed } from '../chromeEmbed';
import sharedContext from '../sharedContext';
import { ContextStore } from '../store/contextStore';
import { errorCode, OnboardingApiError, useOnboardingApi } from './api';
import type { OnboardingState } from './types';

export type OnboardingOpenStatus = 'loading' | 'open' | 'closed' | 'unknown';

/** sharedContext key: the tenant id whose onboarding is closed for good. */
export const CLOSED_KEY = 'onboarding_closed_top';

export const ONBOARDING_PATH = '/onboarding';

/** How long the landing page waits for the status before it goes on as usual. */
export const LANDING_WAIT_MS = 4000;

interface OnboardingStatusValue {
    status: OnboardingOpenStatus;
    state: OnboardingState | null;
    publish: (state: OnboardingState) => void;
    /** A failed read of the state (the page's poll reports its own). */
    refuse: (error: unknown) => void;
    /** True until the first landing of this page load was decided (see useOnboardingLanding). */
    landingPending: React.MutableRefObject<boolean>;
    /** The status took longer than LANDING_WAIT_MS: the landing page stops waiting for it. */
    landingExpired?: boolean;
}

const NO_LANDING = { current: false };

export const OnboardingStatusContext = React.createContext<OnboardingStatusValue>({
    status: 'closed',
    state: null,
    publish: () => {},
    refuse: () => {},
    landingPending: NO_LANDING,
});

export const useOnboardingStatus = () => React.useContext(OnboardingStatusContext);

export const isOpenState = (s: OnboardingState) => s.go_live?.status !== 'live';

export function OnboardingStatusProvider({ children }: { children: React.ReactNode }) {
    const api = useOnboardingApi();
    const { tourOperator, hasPerm, token } = React.useContext(ContextStore) as any;
    const topId = tourOperator?.id ?? null;
    const canRead = !!token && !!hasPerm?.('tour_operator', 'read');
    const closedBefore = topId != null && sharedContext.get(CLOSED_KEY) === topId;
    const [status, setStatus] = React.useState<OnboardingOpenStatus>(() => (canRead && !closedBefore ? 'loading' : 'closed'));
    const [state, setState] = React.useState<OnboardingState | null>(null);
    // The app was loaded on the landing path ("/": every sign-in reloads the app there).
    const landingPending = React.useRef(window.location.pathname === '/' && !isDashboardChromeEmbed());
    const [landingExpired, setLandingExpired] = React.useState(false);
    React.useEffect(() => {
        if (status !== 'loading') {
            return undefined;
        }
        const timer = setTimeout(() => setLandingExpired(true), LANDING_WAIT_MS);
        return () => clearTimeout(timer);
    }, [status]);

    const close = React.useCallback(() => {
        setStatus('closed');
        if (topId != null) {
            sharedContext.set(CLOSED_KEY, topId);
        }
    }, [topId]);

    const publish = React.useCallback((s: OnboardingState) => {
        setState(s);
        if (isOpenState(s)) {
            setStatus('open');
        } else {
            close();
        }
    }, [close]);

    /** Not an easy tenant / no tenant: closed for good. Any other refusal (no permission, which may be
     *  granted later) or an outage only settles a status still being read; nothing is remembered. */
    const refuse = React.useCallback((e: unknown) => {
        if (e instanceof OnboardingApiError && (errorCode(e) === 'not_easy' || e.status === 404)) {
            close();
        } else if (e instanceof OnboardingApiError && e.status >= 400 && e.status < 500) {
            setStatus((prev) => (prev === 'loading' ? 'closed' : prev));
        } else {
            setStatus((prev) => (prev === 'loading' ? 'unknown' : prev));
        }
    }, [close]);

    // The Onboarding page reads the state as it mounts: a page load that starts there leaves the
    // first read to it.
    const pageReadsFirst = React.useRef(window.location.pathname.startsWith(ONBOARDING_PATH));
    React.useEffect(() => {
        if (!canRead || closedBefore) {
            setStatus((prev) => (prev === 'open' ? prev : 'closed'));
            return undefined;
        }
        if (pageReadsFirst.current) {
            pageReadsFirst.current = false;
            return undefined;
        }
        const ctrl = new AbortController();
        api.state(ctrl.signal).then(
            (s) => {
                if (!ctrl.signal.aborted) {
                    publish(s);
                }
            },
            (e) => {
                if (!ctrl.signal.aborted && !(e && e.name === 'AbortError')) {
                    refuse(e);
                }
            },
        );
        return () => ctrl.abort();
        // Once per session and tenant: a permission change re-reads through canRead.
    }, [api, canRead, closedBefore, publish, refuse]);

    const value = React.useMemo(
        () => ({ status, state, publish, refuse, landingPending, landingExpired }),
        [status, state, publish, refuse, landingExpired],
    );
    return <OnboardingStatusContext.Provider value={value}>{children}</OnboardingStatusContext.Provider>;
}

export type LandingDecision = 'hold' | 'onboarding' | 'none';

/** Where the landing page ("/") goes for this user, decided once per page load: while the onboarding
 *  is open, a user who may change it (`tour_operator.write`, the signup admin) lands on the
 *  Onboarding page; `hold` while the status is still being read (so the usual redirect to Chat does
 *  not flash first), at most LANDING_WAIT_MS. Any other path, a later visit to "/", or a user who
 *  may not write: `none`. */
export function useOnboardingLanding(pathname: string): LandingDecision {
    const { status, landingPending, landingExpired } = useOnboardingStatus();
    const { hasPerm } = React.useContext(ContextStore) as any;
    const canWrite = !!hasPerm?.('tour_operator', 'write');
    const waiting = status === 'loading' && !landingExpired;
    let decision: LandingDecision = 'none';
    if (landingPending.current && pathname === '/' && canWrite) {
        if (waiting) {
            decision = 'hold';
        } else if (status === 'open') {
            decision = 'onboarding';
        }
    }
    const decided = decision !== 'hold' && (!waiting || pathname !== '/' || !canWrite);
    React.useEffect(() => {
        if (decided) {
            landingPending.current = false;
        }
    }, [decided, landingPending]);
    return decision;
}
